'use client';

import React, { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { PulseButton } from 'src/components/PulseUI/PulseButton';
import { Dialog } from 'src/components/PulseUI/Dialog';
import { Select, Option } from 'src/components/PulseUI/Select';
import { DatePicker } from 'src/components/PulseUI/DatePicker';
import { PulseTextArea } from 'src/components/PulseUI/PulseTextArea';
import {
  useGetActivityTypesQuery,
  useCreateActivityMutation,
} from 'src/api/redux';
import type { RouteStopResponse } from 'src/api/redux/routes';
import type { VisitedStopActivity } from 'src/redux/slices/routeNavigationSlice';
import { extractErrorMessage } from 'src/components/Strategy/Profiles/utils/errorUtils';

export interface LogStopActivityDialogProps {
  stop: RouteStopResponse;
  onLogged: (stop: RouteStopResponse, activity: VisitedStopActivity) => void;
  onClose: () => void;
}

/**
 * Logs an activity against a stop's provider, then marks the stop visited.
 * Mount it only while a stop is being logged so the form starts fresh.
 */
const LogStopActivityDialog: React.FC<LogStopActivityDialogProps> = ({
  stop,
  onLogged,
  onClose,
}) => {
  const { data: activityTypes } = useGetActivityTypesQuery();
  const [createActivity, { isLoading: isSaving }] = useCreateActivityMutation();

  const [typeId, setTypeId] = useState<number | null>(null);
  const [date, setDate] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const typeOptions = useMemo(
    () =>
      [...(activityTypes?.results ?? [])]
        .filter((type) => !type.deactivated)
        .sort((a, b) => a.label.localeCompare(b.label)),
    [activityTypes]
  );

  const selectedType = typeOptions.find((type) => type.id === typeId);
  const canSave =
    !!selectedType && note.trim().length > 0 && !!date && !isSaving;

  const handleSave = async () => {
    if (!canSave) return;
    setError(null);
    try {
      await createActivity({
        providerId: stop.provider_id,
        data: { type: selectedType.id, details: note.trim(), date },
      }).unwrap();
      onLogged(stop, {
        date,
        note: note.trim(),
        typeLabel: selectedType.label,
      });
      onClose();
    } catch (err) {
      setError(extractErrorMessage(err, "Couldn't log this activity."));
    }
  };

  return (
    <Dialog
      isOpen
      onClose={onClose}
      size="xl"
      aria-labelledby="log-stop-activity-title"
      data-testid="log-stop-activity-dialog"
    >
      <Dialog.Header>
        <h2 className="text-lg font-medium" id="log-stop-activity-title">
          Log Activity
        </h2>
      </Dialog.Header>
      <Dialog.Content>
        <div className="flex flex-col gap-3 p-4">
          <span className="text-sm font-semibold text-neutral-800 truncate">
            {stop.name}
          </span>
          <Select
            value={typeId}
            placeholder="Activity type"
            aria-label="Activity type"
            onChange={(_event, value) => setTypeId(Number(value))}
          >
            {typeOptions.map((type) => (
              <Option key={type.id} value={type.id}>
                {type.label}
              </Option>
            ))}
          </Select>
          <DatePicker
            value={date}
            onChange={setDate}
            aria-label="Activity date"
            data-testid="log-stop-activity-date"
          />
          <PulseTextArea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Add a note about this visit..."
            minRows={3}
            fullWidth
            aria-label="Activity note"
            data-testid="log-stop-activity-note"
          />
          {error && (
            <p className="text-xs text-red-600" role="alert">
              {error}
            </p>
          )}
        </div>
      </Dialog.Content>
      <Dialog.Actions>
        <PulseButton
          variant="outline"
          onClick={onClose}
          disabled={isSaving}
          className="w-full rounded-full"
          data-testid="log-stop-activity-cancel"
        >
          Cancel
        </PulseButton>
        <PulseButton
          variant="solid"
          color="primary"
          onClick={handleSave}
          disabled={!canSave}
          loading={isSaving}
          className="w-full rounded-full"
          data-testid="log-stop-activity-save"
        >
          Log Activity
        </PulseButton>
      </Dialog.Actions>
    </Dialog>
  );
};

export default LogStopActivityDialog;
